import { useEffect, useState } from "react";

/**
 * useUtmParams — Reads utm_source / utm_medium / utm_campaign / utm_content / utm_term
 * from the URL query string on first mount and persists to sessionStorage so downstream
 * opt-in forms (roadmap notify, exit intent, newsletter footer) can attribute the sign-up
 * to the channel that drove the visit — even if the user navigates 3 pages deep before
 * submitting their email.
 *
 * Persistence: sessionStorage (cleared when the tab closes). This is intentional — we want
 * "first-touch attribution for this visit," not "sticky forever." The backend does the
 * long-term first-touch storage on the initial opt-in.
 *
 * Usage:
 *   const utm = useUtmParams();
 *   await api.post("/public/roadmap/notify", { email, track_number, ...utm });
 */

const STORAGE_KEY = "stagvault_utm";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

function readStored() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeStored(obj) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // sessionStorage may be blocked in private mode; silently ignore
  }
}

function readFromUrl() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const out = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) out[k] = v.trim().slice(0, 64);
  });
  return out;
}

export function useUtmParams() {
  const [utm, setUtm] = useState(() => {
    // First-visit priority: URL UTMs win, but merge with any already-stored UTMs so
    // a partial re-entry (e.g. only utm_content on retargeting) doesn't blow away
    // the original source/medium/campaign attribution captured on the first visit.
    const stored = readStored();
    const fromUrl = readFromUrl();
    if (Object.keys(fromUrl).length > 0) {
      const merged = { ...stored, ...fromUrl };
      writeStored(merged);
      return merged;
    }
    return stored;
  });

  useEffect(() => {
    const fromUrl = readFromUrl();
    if (Object.keys(fromUrl).length > 0) {
      const merged = { ...readStored(), ...fromUrl };
      writeStored(merged);
      setUtm(merged);
    }
  }, []);

  return utm;
}

/**
 * readStickyUtm — imperative accessor for the persisted UTMs (sessionStorage).
 * Useful for one-off form submissions (signup, checkout) that fire outside a
 * component render cycle. Returns undefined if the key is missing.
 */
export function readStickyUtm(key) {
  try {
    const stored = readStored();
    return stored[key];
  } catch {
    return undefined;
  }
}
